import React from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useParams } from "react-router-dom";
import PublicRoutes from "./PublicRouter";
import PrivateRoutes from "./PrivateRouter";
import Login from "../Pages/Login";
import Register from "../Pages/Register";
import Dashboard from "../Pages/Dashboard";
import Settings from "../Pages/Settings";
import HubList from "../Pages/Hubs";
import HubDetail from "../Pages/Hubs/HubDetail";
import DepartmentList from "../Pages/Departments";
import DepartmentDetail from "../Pages/Departments/DepartmentDetail";
import RoleList from "../Pages/Roles";
import RoleDetail from "../Pages/Roles/RoleDetail";
import StaffList from "../Pages/Staff";
import StaffFormPage from "../Pages/Staff/StaffFormPage";
import ShiftBoard from "../Pages/Staff/Shift";
import Attendance from "../Pages/Staff/Attendance";
import AttendanceReport from "../Pages/Staff/AttendanceReport";
import AttendanceCalendar from "../Pages/Staff/AttendanceCalendar";
import TimeOff from "../Pages/Staff/TimeOff";
import StaffDetail from "../Pages/Staff/StaffDetail";
import TaskList from "../Pages/Tasks";
import TaskDetail from "../Pages/Tasks/TaskDetail";
import StaffTracking from "../Pages/StaffTracking";
import StaffRoute from "../Pages/StaffTracking/StaffRoute";
import BrandList from "../Pages/VehicleCatalog/BrandList";
import BrandDetail from "../Pages/VehicleCatalog/BrandDetail";
import ModelList from "../Pages/VehicleCatalog/ModelList";
import ModelDetail from "../Pages/VehicleCatalog/ModelDetail";
import VariantList from "../Pages/VehicleCatalog/VariantList";
import VariantDetail from "../Pages/VehicleCatalog/VariantDetail";
import ModelSiteDataPage from "../Pages/VehicleCatalog/ModelSiteDataPage";
import CarList from "../Pages/VehicleFleet/Car/List";
import CarRegister from "../Pages/VehicleFleet/Car/Register";
import CarView from "../Pages/VehicleFleet/Car/View";
import CarDeviceRegister from "../Pages/VehicleFleet/Car/DeviceRegister";
import CarTracking from "../Pages/VehicleFleet/Car/Tracking";
import BikeList from "../Pages/VehicleFleet/Bikes";
import BikeDetail from "../Pages/VehicleFleet/Bikes/BikeDetail";
import ServiceList from "../Pages/VehicleFleet/Services";
import ServiceDetail from "../Pages/VehicleFleet/Services/ServiceDetail";
import CustomerList from "../Pages/Customer";
import CustomerDetail from "../Pages/Customer/CustomerDetail";
import AssociateList from "../Pages/Client/Associates";
import AssociateFormPage from "../Pages/Client/Associates/AssociateFormPage";
import AssociateDetail from "../Pages/Client/Associates/AssociateDetail";
import TicketList from "../Pages/Tickets";
import TicketDetail from "../Pages/Tickets/TicketDetail";
import TicketSettings from "../Pages/Tickets/TicketSettings";
import TicketReports from "../Pages/Tickets/TicketReports";
import FaqList from "../Pages/Client/FAQ";
import BookingList from "../Pages/Booking/List";
import PaymentList from "../Pages/Payment/List";
import BookingAvailability from "../Pages/Booking/Availability";
import BookingRegister from "../Pages/Booking/Register";
import BookingView from "../Pages/Booking/View";
import ProtectionTiers from "../Pages/Booking/Protection";
import ProtectionView from "../Pages/Booking/Protection/View";
import PricingCalendarPage from "../Pages/Booking/PricingCalendar";
import CouponList from "../Pages/Coupon";
import CouponView from "../Pages/Coupon/View";
import Notifications from "../Pages/Notifications";
import NotificationCentre from "../Pages/NotificationCentre";
import SendToStaff from "../Pages/SendToStaff";
import Reviews from "../Pages/Reviews";
import CampaignList from "../Pages/Campaigns";
import CampaignWizard from "../Pages/Campaigns/CampaignWizard";
import CampaignDetail from "../Pages/Campaigns/CampaignDetail";

const LegacyRedirect = ({ to, param, suffix = "" }) => {
    const params = useParams();
    return <Navigate to={`${to}/${params[param]}${suffix}`} replace />;
};

const Index = () => {
    return (
        <Router>
            <Routes>
                <Route element={<PublicRoutes />}>
                    <Route index element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/*" element={<Navigate to="/" replace />} />
                </Route>

                <Route element={<PrivateRoutes />}>
                    {/* List pages keep their page number in the path (/x/page/2) and filters in the query
                        string, so Back from a record returns to the same page and filters (Helpers/useListParams). */}
                    <Route path="/*" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/hubs" element={<HubList />} />
                    <Route path="/hubs/page/:pageNo" element={<HubList />} />
                    <Route path="/hubs/:hubId" element={<HubDetail />} />
                    <Route path="/departments" element={<DepartmentList />} />
                    <Route path="/departments/page/:pageNo" element={<DepartmentList />} />
                    <Route path="/departments/:departmentId" element={<DepartmentDetail />} />
                    <Route path="/roles" element={<RoleList />} />
                    <Route path="/roles/page/:pageNo" element={<RoleList />} />
                    <Route path="/roles/:roleId" element={<RoleDetail />} />
                    <Route path="/staff" element={<StaffList />} />
                    <Route path="/staff/page/:pageNo" element={<StaffList />} />
                    <Route path="/staff/new" element={<StaffFormPage />} />
                    {/* Literal segment - must stay above "/staff/:staffId". */}
                    <Route path="/shift" element={<ShiftBoard />} />
                    <Route path="/attendance" element={<Attendance />} />
                    <Route path="/attendance/page/:pageNo" element={<Attendance />} />
                    <Route path="/attendance/report" element={<AttendanceReport />} />
                    <Route path="/attendance/report/page/:pageNo" element={<AttendanceReport />} />
                    <Route path="/attendance/:staffId" element={<AttendanceCalendar />} />
                    <Route path="/time-off" element={<TimeOff />} />
                    <Route path="/time-off/page/:pageNo" element={<TimeOff />} />
                    <Route path="/staff/:staffId" element={<StaffDetail />} />
                    <Route path="/staff/:staffId/edit" element={<StaffFormPage />} />
                    <Route path="/staff-tracking" element={<StaffTracking />} />
                    <Route path="/staff-tracking/page/:pageNo" element={<StaffTracking />} />
                    <Route path="/staff-tracking/route/:staffId" element={<StaffRoute />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/notifications/page/:pageNo" element={<Notifications />} />
                    <Route path="/notification-centre" element={<NotificationCentre />} />
                    <Route path="/notification-centre/:section" element={<NotificationCentre />} />
                    <Route path="/notification-centre/:section/:sub" element={<NotificationCentre />} />
                    <Route path="/send-to-staff" element={<SendToStaff />} />
                    <Route path="/reviews" element={<Reviews />} />
                    <Route path="/campaigns" element={<CampaignList />} />
                    <Route path="/campaigns/new" element={<CampaignWizard />} />
                    <Route path="/campaigns/:campaignId/edit" element={<CampaignWizard />} />
                    <Route path="/campaigns/:campaignId" element={<CampaignDetail />} />
                    <Route path="/tasks" element={<TaskList />} />
                    <Route path="/tasks/page/:pageNo" element={<TaskList />} />
                    <Route path="/tasks/:taskId" element={<TaskDetail />} />
                    {/* Catalog: three independent flows as of 2026-09-16.
                        Brand, Model and Variant each have their own sidebar
                        entry, list and detail page - they are no longer a
                        single brand -> model -> variant drill-down. */}
                    <Route path="/brand" element={<BrandList />} />
                    <Route path="/brand/page/:pageNo" element={<BrandList />} />
                    <Route path="/brand/:brandId" element={<BrandDetail />} />
                    <Route path="/model" element={<ModelList />} />
                    <Route path="/model/page/:pageNo" element={<ModelList />} />
                    <Route path="/model/:modelId" element={<ModelDetail />} />
                    <Route path="/model/:modelId/site-data" element={<ModelSiteDataPage />} />
                    <Route path="/variant" element={<VariantList />} />
                    <Route path="/variant/page/:pageNo" element={<VariantList />} />
                    <Route path="/variant/:variantId" element={<VariantDetail />} />

                    {/* Legacy /vehicle-catalog/* URLs - kept so existing
                        bookmarks and any stale links still land correctly. */}
                    <Route path="/vehicle-catalog/brands" element={<Navigate to="/brand" replace />} />
                    <Route path="/vehicle-catalog/brands/:brandId" element={<LegacyRedirect to="/brand" param="brandId" />} />
                    <Route path="/vehicle-catalog/models/:modelId" element={<LegacyRedirect to="/model" param="modelId" />} />
                    <Route path="/vehicle-catalog/models/:modelId/site-data" element={<LegacyRedirect to="/model" param="modelId" suffix="/site-data" />} />
                    <Route path="/vehicle-catalog/variants/:variantId" element={<LegacyRedirect to="/variant" param="variantId" />} />
                    {/* Car flow. These URLs are the ones the Car/ pages navigate to
                        themselves (/car, /car/:pageNo, /car/register, /car/details/:id,
                        /car/update/:id, /car/device/:id, /car/tracking/:id) - changing
                        them would mean rewriting every navigate() call in those files. */}
                    <Route path="/car" element={<CarList />} />
                    <Route path="/car/register" element={<CarRegister />} />
                    <Route path="/car/register/:associateId" element={<CarRegister />} />
                    <Route path="/car/details/:id" element={<CarView />} />
                    <Route path="/car/update/:id" element={<CarRegister />} />
                    <Route path="/car/device/:id" element={<CarDeviceRegister />} />
                    <Route path="/car/tracking/:id" element={<CarTracking />} />
                    <Route path="/car/:pageNo" element={<CarList />} />
                    {/* Old fleet URLs keep working */}
                    <Route path="/vehicle-fleet/cars" element={<Navigate to="/car" replace />} />
                    <Route path="/vehicle-fleet/cars/new" element={<Navigate to="/car/register" replace />} />
                    <Route path="/vehicle-fleet/cars/:carId" element={<LegacyRedirect to="/car/details" param="carId" />} />
                    <Route path="/vehicle-fleet/bikes" element={<BikeList />} />
                    <Route path="/vehicle-fleet/bikes/page/:pageNo" element={<BikeList />} />
                    <Route path="/vehicle-fleet/bikes/:bikeId" element={<BikeDetail />} />
                    <Route path="/vehicle-fleet/services" element={<ServiceList />} />
                    <Route path="/vehicle-fleet/services/page/:pageNo" element={<ServiceList />} />
                    <Route path="/vehicle-fleet/services/:serviceId" element={<ServiceDetail />} />
                    <Route path="/customers" element={<CustomerList />} />
                    <Route path="/customers/page/:pageNo" element={<CustomerList />} />
                    <Route path="/customers/:customerId" element={<CustomerDetail />} />
                    <Route path="/associates" element={<AssociateList />} />
                    <Route path="/associates/page/:pageNo" element={<AssociateList />} />
                    <Route path="/associates/new" element={<AssociateFormPage />} />
                    <Route path="/associates/:associateId" element={<AssociateDetail />} />
                    <Route path="/associates/:associateId/edit" element={<AssociateFormPage />} />
                    <Route path="/tickets" element={<TicketList />} />
                    <Route path="/tickets/settings" element={<TicketSettings />} />
                    <Route path="/tickets/reports" element={<TicketReports />} />
                    <Route path="/tickets/:ticketId" element={<TicketDetail />} />
                    <Route path="/faq" element={<FaqList />} />
                    <Route path="/faq/page/:pageNo" element={<FaqList />} />
                    <Route path="/booking" element={<BookingList />} />
                    <Route path="/booking/page/:pageNo" element={<BookingList />} />
                    <Route path="/booking/availability" element={<BookingAvailability />} />
                    <Route path="/booking/register" element={<BookingRegister />} />
                    {/* Matches the URL BookingDetails.js (Car -> Bookings tab) and the
                        Booking List/Customer bookings rows already navigate to. */}
                    <Route path="/booking/details/:bookingId" element={<BookingView />} />
                    <Route path="/payments" element={<PaymentList />} />
                    <Route path="/payments/page/:pageNo" element={<PaymentList />} />
                    <Route path="/protection" element={<ProtectionTiers />} />
                    <Route path="/protection/page/:pageNo" element={<ProtectionTiers />} />
                    <Route path="/protection/:tpId" element={<ProtectionView />} />
                    <Route path="/pricing-calendar" element={<PricingCalendarPage />} />
                    <Route path="/coupon" element={<CouponList />} />
                    <Route path="/coupon/page/:pageNo" element={<CouponList />} />
                    <Route path="/coupon/:couponCode" element={<CouponView />} />
                </Route>
            </Routes>
        </Router>
    );
};

export default Index;
